import { Tooltip, Typography } from "@mui/material";
import { Flex } from "../Flex";
import { SpaceSummaryCardProps } from "./SpaceSummaryCard";
import { formatGroupNames } from "~/lib/format-asset-list-names";
import { getGroupIcon } from "../grouping-button";
import { ViewType } from "../view-switcher";

export type SpaceSummaryAssetGroupButtonsProps = {
  viewType: ViewType;
  assetGroupStats: SpaceSummaryCardProps["summaryData"]["assetGroupStats"];
};

export function SpaceSummaryAssetGroupButtons({
  viewType,
  assetGroupStats,
}: SpaceSummaryAssetGroupButtonsProps) {
  const previewGroupTypes = ["k8s", "azure", "aws", "github"] as const;
  const activeAssetGroups =
    assetGroupStats
      .flatMap((ag) => (ag.count > 0 ? ag : []))
      .sort((a, b) => b.count - a.count) || [];
  const maxCount = 6;
  const firstAssetGroups = activeAssetGroups.slice(0, maxCount);
  const remainingAssetGroups = activeAssetGroups.slice(maxCount);

  const firstAssetButtons = firstAssetGroups
    .slice(0, remainingAssetGroups.length > 0 ? maxCount - 1 : maxCount)
    .map((ag) => (
      <Tooltip
        key={ag.groupType}
        title={formatGroupNames(ag)}
        arrow
        placement="top"
        disableInteractive
      >
        {viewType === ViewType.List ? (
          <Flex
            key={ag.groupType}
            sx={{
              p: 0.5,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getGroupIcon(ag.groupType, { fontSize: "24px" })}
          </Flex>
        ) : (
          <Flex
            key={ag.groupType}
            sx={{
              flexDirection: "column",
              minWidth: `calc(100% / ${maxCount})`,
              alignItems: "center",
              justifyContent: "center",
              p: 1,
            }}
          >
            {getGroupIcon(ag.groupType, { fontSize: "24px" })}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              {ag.count}
            </Typography>
          </Flex>
        )}
      </Tooltip>
    ));

  const remainingAssetButton =
    remainingAssetGroups.length > 0 &&
    (viewType === ViewType.List ? (
      <Flex
        sx={{
          minWidth: 32,
          height: 32,
          color: "text.secondary",
          bgcolor: "background.light",
          fontSize: 12,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
        }}
      >
        +{remainingAssetGroups.length + 1}
      </Flex>
    ) : (
      <Flex
        sx={{
          flexDirection: "column",
          minWidth: `calc(100% / ${maxCount})`,
          color: "text.secondary",
          bgcolor: "background.light",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          borderRadius: 1,
        }}
      >
        +{remainingAssetGroups.length + 1}
      </Flex>
    ));

  const integrationsPreview = (
    <Flex>
      {previewGroupTypes.map((groupType) => (
        <Flex
          key={groupType}
          sx={{
            bgcolor: "background.lightest",
            borderRadius: "50%",
            border: "1px solid",
            borderColor: "background.light",
            mx: -0.3,
            width: 24,
            height: 24,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {getGroupIcon(groupType, { fontSize: "14px" })}
        </Flex>
      ))}
    </Flex>
  );

  const noIntegrationsText = "No active integrations";
  const noIntegrationsButton =
    viewType === ViewType.List ? (
      <Flex sx={{ alignItems: "center", gap: 1, minHeight: 32 }}>
        <Typography sx={{ color: "text.secondary" }}>
          {noIntegrationsText}
        </Typography>
      </Flex>
    ) : (
      <Flex
        sx={{
          flexDirection: "column",
          textTransform: "none",
          bgcolor: "background.light",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          borderRadius: 1,
        }}
      >
        {integrationsPreview}
        <Typography
          sx={{ fontSize: 12, fontWeight: 700, color: "text.disabled" }}
        >
          {noIntegrationsText}
        </Typography>
      </Flex>
    );

  return (
    <>
      {activeAssetGroups.length > 0 && (
        <Flex sx={{ flexWrap: "nowrap", mx: -0.5 }}>
          {firstAssetButtons}
          {remainingAssetButton}
        </Flex>
      )}
      {activeAssetGroups.length === 0 && noIntegrationsButton}
    </>
  );
}
